import { type InjectionKey, type ComputedRef, inject, provide, computed, unref, type MaybeRef } from 'vue'

export interface Context {
  uiBlockSize?: '24' | '40' | '48'
  uiOvershootNone?: boolean
  uiBleedInline?: boolean
  uiSunken?: boolean
  uiRaised?: boolean
}

const ContextKey: InjectionKey<ComputedRef<Context>> = Symbol('Context')

/**
 * Get context. Optionally, mix in overrides.
 *
 * @param c - Object or ref
 * @example useContext({ uiBlockSize: '40' }) // send to descendants
 * @example const ancestors = useContext() // no sending, just asking
 * @example const newContext = useContext({ uiBlockSize: '40' }) // both
 * @example useContext(computed(() => ({ uiBlockSize: props.uiBlockSize }))) // recompute descendant context when prop changes
 */
export const useContext = (c: MaybeRef<Partial<Context>> = {}) => {
  const parentContext = inject(ContextKey, computed(() => ({})))
  const childContext = computed(() => ({
    ...parentContext.value,
    ...unref(c)
  }))
  provide(ContextKey, childContext)

  return childContext
}
